import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import urls from "../../../urls";
import type { Paragraph6GemeindeAnteilErzeugerPayload } from "../Paragraph6.types";

export function useParagraph6GemeindeAnteilErzeugerMutations(
  variantId: number,
  contractId: string
) {
  const queryClient = useQueryClient();

  function invalidateRelatedContracts(contractId: string) {
    queryClient.invalidateQueries({
      queryKey: ["paragraph6Contract", { contractId }]
    });
    queryClient.invalidateQueries({
      queryKey: ["paragraph6Contracts", { variantId }]
    });
  }

  function invalidateCreditsAndErrors() {
    queryClient.invalidateQueries({
      queryKey: ["paragraph6CreditErrors", { variantId }]
    });
    queryClient.invalidateQueries({
      queryKey: ["paragraph6CreatableCredits", { variantId }]
    });
  }

  const paragraph6GemeindeAnteilErzeugerCreateMutation = useMutation({
    mutationFn: (generator: Paragraph6GemeindeAnteilErzeugerPayload) =>
      createParagraph6GemeindeAnteilErzeuger(generator, contractId),
    onSuccess: (_, generator) => {
      queryClient.invalidateQueries({
        queryKey: [
          "paragraph6UnassignedGenerators",
          { contractId: generator.contract }
        ]
      });
      if (generator.contract) invalidateRelatedContracts(generator.contract);
      invalidateCreditsAndErrors();
    }
  });

  const paragraph6GemeindeAnteilErzeugerEditMutation = useMutation({
    mutationFn: (generator: Paragraph6GemeindeAnteilErzeugerPayload) =>
      editParagraph6GemeindeAnteilErzeuger(generator, contractId),
    onSuccess: (_, generator) => {
      queryClient.invalidateQueries({
        queryKey: [
          "paragraph6GemeindeAnteilErzeuger",
          { generatorShareId: generator.generator }
        ]
      });
      if (generator.contract) invalidateRelatedContracts(generator.contract);
      invalidateCreditsAndErrors();
    }
  });

  const paragraph6GemeindeAnteilErzeugerDeleteMutation = useMutation({
    mutationFn: (generatorId: number) =>
      deleteParagraph6GemeindeAnteilErzeuger(generatorId, contractId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["paragraph6UnassignedGenerators", { contractId }]
      });
      invalidateRelatedContracts(contractId ?? "");
      invalidateCreditsAndErrors();
    }
  });

  return {
    paragraph6GemeindeAnteilErzeugerCreateMutation,
    paragraph6GemeindeAnteilErzeugerEditMutation,
    paragraph6GemeindeAnteilErzeugerDeleteMutation
  };
}

function createParagraph6GemeindeAnteilErzeuger(
  generator: Paragraph6GemeindeAnteilErzeugerPayload,
  contractId: string
) {
  const paragraph6GemeindeAnteilErzeugereUrl =
    urls.apiWithoutCamelization.paragraph6GemeindeAnteileErzeuger(contractId);
  return apiWithoutCamelization.post<Paragraph6GemeindeAnteilErzeugerPayload>(
    paragraph6GemeindeAnteilErzeugereUrl,
    generator
  );
}

function editParagraph6GemeindeAnteilErzeuger(
  generator: Paragraph6GemeindeAnteilErzeugerPayload,
  contractId: string
) {
  if (!generator.generator) {
    throw new Error("Cannot edit generator share without an id");
  }
  const paragraph6GemeindeAnteilErzeugerUrl =
    urls.apiWithoutCamelization.paragraph6GemeindeAnteilErzeuger(
      contractId,
      generator.generator
    );
  return apiWithoutCamelization.patch<Paragraph6GemeindeAnteilErzeugerPayload>(
    paragraph6GemeindeAnteilErzeugerUrl,
    generator
  );
}

function deleteParagraph6GemeindeAnteilErzeuger(
  generatorId: number,
  contractId: string
) {
  const paragraph6GemeindeAnteilErzeugerUrl =
    urls.apiWithoutCamelization.paragraph6GemeindeAnteilErzeuger(
      contractId,
      generatorId
    );
  return apiWithoutCamelization.delete(paragraph6GemeindeAnteilErzeugerUrl);
}
